import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle, Send } from "lucide-react";
import http from "@/lib/api";
import { toast } from "sonner";

const TYPES = ["Kafé", "Restaurant", "Bensinstasjon", "Hotell", "Kontor", "Storkjøkken", "Annet"];
const EMPTY = { company: "", type: "Kafé", name: "", email: "", phone: "", place: "", machine: "", message: "" };

const Field = ({ label, children }) => <div><Label className="text-sm">{label}</Label><div className="mt-1">{children}</div></div>;

export function BusinessQuickForm({ source }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company || !f.name || !f.email || !f.message) { toast.error("Fyll ut firmanavn, kontaktperson, e-post og beskrivelse."); return; }
    setBusy(true);
    const message = `[Bedriftsforespørsel – ${source}]\nFirma: ${f.company}\nBransje: ${f.type}\nSted: ${f.place || "—"}\nMaskin: ${f.machine || "—"}\n\n${f.message}`;
    try {
      await http.post("/v1/contact-messages", { name: f.name, email: f.email, phone: f.phone, message });
      setDone(true);
    } catch { toast.error("Noe gikk galt. Prøv igjen eller ring 90 23 22 06."); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white rounded-xl border border-[#D4A574]/30 p-8 text-center" data-testid="bqf-success">
        <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-3" />
        <p className="font-semibold text-[#3D2314]">Takk! Forespørselen er mottatt.</p>
        <p className="text-sm text-[#1A1A1A]/70 mt-1">Vi tar kontakt så snart vi kan, vanligvis innen én virkedag.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-[#D4A574]/30 p-6 md:p-8 space-y-4 shadow-sm" data-testid="bqf-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Firmanavn *"><Input value={f.company} onChange={set("company")} data-testid="bqf-company" /></Field>
        <Field label="Bransje">
          <select value={f.type} onChange={set("type")} data-testid="bqf-type" className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Kontaktperson *"><Input value={f.name} onChange={set("name")} data-testid="bqf-name" /></Field>
        <Field label="Sted / kommune"><Input value={f.place} onChange={set("place")} placeholder="F.eks. Bergen sentrum" data-testid="bqf-place" /></Field>
        <Field label="E-post *"><Input type="email" value={f.email} onChange={set("email")} data-testid="bqf-email" /></Field>
        <Field label="Telefon"><Input type="tel" value={f.phone} onChange={set("phone")} data-testid="bqf-phone" /></Field>
      </div>
      <Field label="Maskin (merke og modell)"><Input value={f.machine} onChange={set("machine")} placeholder="F.eks. La Marzocco Linea, Jura GIGA" data-testid="bqf-machine" /></Field>
      <Field label="Hva trenger dere hjelp med? *"><Textarea rows={4} value={f.message} onChange={set("message")} placeholder="Beskriv feilen eller ønsket service" data-testid="bqf-message" /></Field>
      <Button type="submit" disabled={busy} data-testid="bqf-submit" className="w-full bg-[#3D2314] hover:bg-[#3D2314]/90 text-white"><Send className="h-4 w-4 mr-2" />{busy ? "Sender..." : "Send bedriftsforespørsel"}</Button>
      <p className="text-xs text-[#1A1A1A]/50">Forespørselen er uforpliktende. Endelig pris oppgis aldri før diagnostikk.</p>
    </form>
  );
}
